/**
 * Read-only tenant offers table (TMMPDM-0007).
 * TMMPDM-0032 / TMMPDM-0086: a Rate card column. `resellerRateCardId` is a customer-card version
 * id, named from the customer rate card that owns that version; "—" when absent. A missing
 * version stays "Rate card {{id}}". The list is display only: no offer is filtered or judged by it.
 */
import { useEffect, useState } from 'react';
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { Amount, HelpPopover } from '@/components/ui';
import type { OfferView } from '@/api/tmm/types';
import { cardsByVersionId } from '../customerCards/customerRateCardModel';
import { formatInstant, formatPercentBp, formatSeatBands } from '@/format';

interface OffersPanelProps {
  slug: string;
  /** The tenant's reseller, whose card list names each offer's rate card; `null` → none. */
  resellerId: number | null;
}

export function OffersPanel({ slug, resellerId }: OffersPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<OfferView[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [cardNames, setCardNames] = useState<Map<number, string>>(() => new Map());

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listOffers(slug)
      .then((list) => {
        if (!cancelled) {
          setRows(list.offers);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  // Version id → the customer card's name. A failed read leaves the ids unnamed, never the table.
  useEffect(() => {
    if (resellerId === null) return;
    let cancelled = false;
    adminApi
      .listCustomerRateCards()
      .then((list) => {
        if (cancelled) return;
        const names = new Map<number, string>();
        for (const [versionId, card] of cardsByVersionId(list.rateCards)) {
          names.set(versionId, card.name);
        }
        setCardNames(names);
      })
      .catch(() => {
        if (!cancelled) setCardNames(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, reloadToken]);

  const rateCardName = (offer: OfferView) =>
    cardNames.get(offer.resellerRateCardId) ??
    t('tenantDetail.offers.rateCardById', 'Rate card {{id}}', {
      id: offer.resellerRateCardId,
    });

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('tenantDetail.offers.loadError', 'Could not load offers.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={t('tenantDetail.offers.tableAria', 'Tenant offers')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('tenantDetail.offers.col.id', 'Offer')}</TableCell>
            <TableCell>{t('tenantDetail.offers.col.status', 'Status')}</TableCell>
            <TableCell>{t('tenantDetail.offers.col.rateCard', 'Rate card')}</TableCell>
            <TableCell align="right">
              {t('tenantDetail.offers.col.retailBase', 'Retail base')}
            </TableCell>
            <TableCell>{t('tenantDetail.offers.col.seatBands', 'Seat bands')}</TableCell>
            <TableCell>
              {t('tenantDetail.offers.col.aiMarkup', 'AI markup (customer total)')}
              <HelpPopover
                label={t('tenantDetail.offers.col.aiMarkup', 'AI markup (customer total)')}
                short={t(
                  'tenantDetail.offers.aiMarkupHelp',
                  "What the customer pays over the AI provider's cost: our markup combined with the reseller's.",
                )}
              />
            </TableCell>
            <TableCell>{t('tenantDetail.offers.col.trial', 'Trial (new / guest)')}</TableCell>
            <TableCell align="right">
              {t('tenantDetail.offers.col.subscribers', 'Subscribers')}
            </TableCell>
            <TableCell>{t('tenantDetail.offers.col.created', 'Created')}</TableCell>
            <TableCell>{t('tenantDetail.offers.col.closed', 'Closed')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows === null ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                {t('common.loading', 'Loading')}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <EmptyState title={t('tenantDetail.offers.empty', 'No offers yet.')} />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((offer) => (
              <TableRow key={offer.id}>
                <TableCell>{offer.id}</TableCell>
                <TableCell>
                  {offer.current ? (
                    <Chip size="small" label={t('tenantDetail.offers.current', 'Current')} />
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{rateCardName(offer)}</TableCell>
                <TableCell align="right">
                  <Amount minor={offer.baseRetailMinor} currency={offer.currency} locale={locale} />
                </TableCell>
                <TableCell>{formatSeatBands(offer.seatBands, offer.currency, locale)}</TableCell>
                <TableCell>{formatPercentBp(offer.aiMarkupBp, locale, 1)}</TableCell>
                <TableCell>
                  {offer.trialDaysNewSubscriber} / {offer.trialDaysGuestConversion}
                </TableCell>
                <TableCell align="right">
                  <Amount count={offer.subscriberCount} locale={locale} />
                </TableCell>
                <TableCell>{formatInstant(offer.createdAt, locale, 'date')}</TableCell>
                <TableCell>
                  {offer.closedAt ? formatInstant(offer.closedAt, locale, 'date') : '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
